import { Switch } from '@mui/material';
import type { SwitchProps } from '@mui/material';
import React, { useRef } from 'react';

const DRAG_THRESHOLD_PX = 6;

/**
 * A Switch that also responds to dragging the thumb, as people expect from a
 * slide toggle. A drag right turns it on and a drag left turns it off; a plain
 * tap still toggles as usual.
 */
export default function SwipeSwitch({
  onChange,
  onPointerDown,
  sx,
  ...props
}: SwitchProps) {
  const suppressClick = useRef(false);
  const checkedRef = useRef(false);
  checkedRef.current = Boolean(props.checked);

  const handlePointerDown = (event: React.PointerEvent<HTMLSpanElement>) => {
    onPointerDown?.(event as unknown as React.PointerEvent<HTMLButtonElement>);
    if (props.disabled) return;
    const startX = event.clientX;
    let dragged = false;

    const handleMove = (move: PointerEvent) => {
      if (Math.abs(move.clientX - startX) > DRAG_THRESHOLD_PX) dragged = true;
    };
    const finish = (up: PointerEvent) => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      if (!dragged || up.type === 'pointercancel') return;
      // The browser may still fire a click for this gesture; ignore it.
      suppressClick.current = true;
      setTimeout(() => {
        suppressClick.current = false;
      }, 0);
      const next = up.clientX > startX;
      if (next !== checkedRef.current) {
        onChange?.(
          { target: { checked: next } } as React.ChangeEvent<HTMLInputElement>,
          next,
        );
      }
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  };

  return (
    <Switch
      {...props}
      onPointerDown={handlePointerDown}
      onChange={(event, checked) => {
        if (suppressClick.current) return;
        onChange?.(event, checked);
      }}
      // Keep the browser from taking horizontal drags as a scroll or pan.
      sx={[{ touchAction: 'pan-y' }, ...(Array.isArray(sx) ? sx : [sx])]}
    />
  );
}
